import axios, { AxiosResponse, CancelToken, CancelTokenSource } from "axios"
import { InsertTarjetaBancariaCommandProps } from "./Commands/InsertTarjetaBancariaCommand"
import { UpdateTarjetaBancariaCommandProps } from "./Commands/UpdateTarjetaBancariaCommand"
import { GetMovimientosTarjetaBancariaByFiltersDtoProps } from "./DTOs/GetMovimientosTarjetaBancariaByFiltersDto"
import { GetTarjetasBancariasByFiltersDtoProps } from "./DTOs/GetTarjetasBancariasByFiltersDto"
import { GetTarjetasBancariasByIdDtoProps } from "./DTOs/GetTarjetasBancariasByIdDto"
import { GetMovimientosTarjetaBancariaByFiltersDtoIPagedEnumerable } from "./Schemas/GetMovimientosTarjetaBancariaByFiltersDtoIPagedEnumerable"
import { GetTarjetasBancariasByFiltersDtoIPagedEnumerableProps } from "./Schemas/GetTarjetasBancariasByFiltersDtoIPagedEnumerableProps"
import {
  MovimientoTarjetaAdapter,
  TarjetaBancariaByIdAdapter,
  tarjetaBancariaByFiltersAdapter,
} from "./TarjetaBancaria.adapter"
import { TipoTransaccionType } from "./Types/TipoTransaccionEnum"
import { SortingCriteria } from "./interfaces/SortingCriteria"

export interface GetTarjetaBancariaQueryParamsProps {
  Keyword?: string
  SortingCriteria?: SortingCriteria | string
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}

/**
 * Retrieves a list of tarjetas bancarias based on the specified query parameters.
 * @param queryParams - The query parameters for filtering the tarjetas bancarias.
 * @returns A promise that resolves to a paged enumerable of tarjetas bancarias.
 */
export async function getTarjetaBancaria(
  queryParams?: GetTarjetaBancariaQueryParamsProps,
  cancelToken?: CancelToken
): Promise<GetTarjetasBancariasByFiltersDtoIPagedEnumerableProps> {
  const {
    data,
  }: { data: GetTarjetasBancariasByFiltersDtoIPagedEnumerableProps } =
    await axios.get(`/TarjetaBancaria`, {
      params: queryParams,
      cancelToken,
    })

  return {
    currentPage: data.currentPage,
    items: data.items.map((tarjeta: GetTarjetasBancariasByFiltersDtoProps) =>
      tarjetaBancariaByFiltersAdapter(tarjeta)
    ),
    totalCount: data.totalCount,
  }
}

export async function insertTarjetaBancaria(
  requestBody: InsertTarjetaBancariaCommandProps
) {
  const response = await axios.post(`/TarjetaBancaria`, requestBody)
  return response
}

export interface GetMovimientosTarjetaBancariaQueryParamsProps {
  Id: string
  AmountLowerLimit?: number
  AmountUpperLimit?: number
  BookingDateLowerLimit?: string
  BookingDateUpperLimit?: string
  Keyword?: string
  Type?: TipoTransaccionType
  SortingCriteria?: SortingCriteria
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}

// eslint-disable-next-line import/no-mutable-exports
export let movimientosTarjetaByIdFiltersCall: CancelTokenSource
export async function getMovimientosTarjetaById(
  id: string,
  queryParams: GetMovimientosTarjetaBancariaQueryParamsProps
) {
  if (typeof movimientosTarjetaByIdFiltersCall !== typeof undefined) {
    movimientosTarjetaByIdFiltersCall.cancel("Previous Request Cancelled")
  }
  movimientosTarjetaByIdFiltersCall = axios.CancelToken.source()
  const {
    data,
  }: { data: GetMovimientosTarjetaBancariaByFiltersDtoIPagedEnumerable } =
    await axios.get(`/TarjetaBancaria/${id}/movimientos`, {
      params: queryParams,
      cancelToken: movimientosTarjetaByIdFiltersCall.token,
    })

  return {
    currentPage: data.currentPage,
    items: data.items?.map(
      (movimiento: GetMovimientosTarjetaBancariaByFiltersDtoProps) =>
        MovimientoTarjetaAdapter(movimiento)
    ),
    totalCount: data.totalCount,
  }
}

export async function getTarjetaBancariaById(id: string) {
  const { data }: { data: GetTarjetasBancariasByIdDtoProps } = await axios.get(
    `/TarjetaBancaria/${id}`
  )

  return TarjetaBancariaByIdAdapter(data)
}

export async function deleteTarjetaBancaria(
  id: string
): Promise<AxiosResponse> {
  const response = await axios.delete(`/TarjetaBancaria/${id}`)
  return response
}

export async function getSyncTarjetaBancariaById(
  id: string
): Promise<AxiosResponse> {
  const resp = await axios.post(`/TarjetaBancaria/${id}/sincronizar`)
  return resp
}

export async function putTarjetaBancariaId(
  requestBody: UpdateTarjetaBancariaCommandProps
): Promise<AxiosResponse> {
  const resp = await axios.put(
    `/TarjetaBancaria/${requestBody.id}`,
    requestBody
  )
  return resp
}

interface MovTarjetaExportarExcelQueryParamsProps {
  FechaLowerLimit: string
  FechaUpperLimit: string
}

export async function getMovimientosTarjetaExportarExcel(
  id: string,
  queryParams: MovTarjetaExportarExcelQueryParamsProps
) {
  return axios.get(`/tarjetaBancaria/${id}/movimientos/exportar-excel`, {
    params: queryParams,
    responseType: "blob",
  })
}
